import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  InputGroup as ChakraInputGroup,
  InputLeftElement,
  Switch,
  useBoolean,
} from '@chakra-ui/react'
import { PersonDto } from 'BrokerTsLib/src/dtos'
import { useContext, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Datepicker } from '../components/Datepicker'
import { FormLabel } from '../components/FormLabel'
import { InputGroup } from '../components/InputGroup'
import { SwitchControl } from '../components/SwitchControl'
import { useNameof } from '../hooks/useNameof'
import { useFindPerson } from '../hooks/useUser'
import { Calendar, Cloud } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'
import { CountrySelect } from './CountrySelect'

const BCNForm = ({ personPrefix, disabled }: { personPrefix?: string, disabled?: boolean }) => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const isDisabled = !!insuranceParams?.generalParams.contractNumber || disabled

  const nameof = useNameof<PersonDto>()
  const pnameof = useNameof<PersonDto>(personPrefix)
  const { register, getFieldState, watch, setValue, getValues, trigger } = useFormContext()

  const bcnPath = pnameof(x => x.birthNumber)

  const [findPersonError, setfindPersonError] = useState(false)
  const { mutateAsync: findPerson, isLoading: isLoading } = useFindPerson()

  var bcn = watch(bcnPath) as string ?? ''

  const rcTooltip = isDisabled ? 'Rodné číslo' :
    'Funkce načíst údaje nekontroluje správnost zadaných/doplněných údajů. Údaje jsou doplněny na základě hodnot z předchozích nabídek/smluv z OKappek. Funkce neumožňuje doplnění dat z IS (OKIS,Očko, apod.)'

  const handleFindPerson = () =>
    findPerson(bcn)
      .then((resp) => {
        var person = resp.data
        setfindPersonError(!person)
        if (!!person) {
          var orig = getValues(personPrefix ?? '')
          setValue(personPrefix ?? '', {
            ...person,
            [nameof(x => x.id)]: orig.id,
            [nameof(x => x.type)]: orig.type,
          })
          trigger(pnameof(x => x.address) + '.' + nameof(x => x.address.street))
          trigger(pnameof(x => x.address) + '.' + nameof(x => x.address.houseNumber))
        }
      })

  return (
    <Flex>
      <FormControl isRequired>
        <FormLabel tooltip={rcTooltip} htmlFor="bcn">
          Rodné číslo
        </FormLabel>

        <Flex sx={{ columnGap: 8, rowGap: 2, flexWrap: 'wrap', alignItems: 'start' }}>
          <FormControl isInvalid={!!getFieldState(bcnPath).error}>
            <InputGroup
              isDisabled={isDisabled}
              autoComplete='on'
              id="bcn"
              placeholder="Např. 7505250000"
              htmlSize={16}
              errors={getFieldState(bcnPath).error}
              {...register(bcnPath)}
            />

            {typeof getFieldState(bcnPath).error === 'undefined' && (
              <FormHelperText>Zadejte číslice bez mezer, lomítek či pomlček.</FormHelperText>
            )}
          </FormControl>
        </Flex>
      </FormControl>

      <FormControl isInvalid={findPersonError} ml={2}>
        <FormLabel>&nbsp;</FormLabel>

        {!isDisabled &&
          <Button
            type="button"
            variant="outline"
            isLoading={isLoading}
            isDisabled={isLoading || bcn.length < 9 || bcn.length > 10}
            onClick={handleFindPerson}
          >
            <Cloud color="secondary.500" />
            Načíst údaje
          </Button>
        }

        {!findPersonError && <FormHelperText>&nbsp;</FormHelperText>}
        {findPersonError && <FormErrorMessage justifySelf='end'>RČ nenalezeno, zadejte údaje ručně.</FormErrorMessage>}
      </FormControl>
    </Flex>
  )
}

const DOBNationalityForm = ({ personPrefix, disabled }: { personPrefix?: string, disabled?: boolean }) => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const isDisabled = !!insuranceParams?.generalParams.contractNumber || disabled

  const pnameof = useNameof<PersonDto>(personPrefix)

  const { register, getValues, setValue, watch } = useFormContext()

  return (
    <Flex sx={{ gap: 8 }}>
      <FormControl isRequired>
        <FormLabel htmlFor="pnameof(x => x.birthday)">Datum narození</FormLabel>

        <ChakraInputGroup>
          <InputLeftElement pointerEvents="none" children={<Calendar width={4} height={4} />} />
          {/* <Input id="pnameof(x => x.birthday)" type="date" {...register(pnameof(x => x.birthday))} /> */}
          <Datepicker
            isDisabled={isDisabled}
            date={getValues(pnameof(x => x.birthday))}
            onDateChange={(date) => setValue(pnameof(x => x.birthday), date)}
          />
        </ChakraInputGroup>
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Státní občanství</FormLabel>

        <CountrySelect {...register(pnameof(x => x.nationalityId))} isDisabled={isDisabled} />
      </FormControl>
    </Flex>
  )
}

export const BCNOptions = ({ personPrefix, disabled }: { personPrefix: string, disabled?: boolean }) => {
  const { insuranceParams } = useContext(insuranceParamsContext)
  const isDisabled = !!insuranceParams?.generalParams.contractNumber || disabled

  const pnameof = useNameof<PersonDto>(personPrefix)
  const {
    register,
    getValues
  } = useFormContext()

  const [hasBCN, setHasBCN] = useBoolean(!getValues(pnameof(x => x.isForeigner)))

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <FormControl>
        <FormLabel
          tooltip='U osob, kteří ho nemají přiděleno (cizí státní příslušníci), zaškrtněte volbu "Nemá rodné číslo" a zadejte datum narození a státní občanství.'
        >
          Rodné číslo
        </FormLabel>

        <SwitchControl>
          <Switch
            isChecked={!hasBCN}
            {...register(pnameof(x => x.isForeigner), {
              onChange: () => {
                setHasBCN.toggle()
              }
            })}
            isDisabled={isDisabled}
          />
          <FormLabel htmlFor={pnameof(x => x.isForeigner)} variant="switch">
            Nemá rodné číslo
          </FormLabel>
        </SwitchControl>
      </FormControl>

      {hasBCN && <BCNForm personPrefix={personPrefix} disabled={isDisabled} />}

      {!hasBCN && <DOBNationalityForm personPrefix={personPrefix} disabled={isDisabled} />}
    </Box>
  )
}
